import Paper from '@/components/Paper'
import {
    createCurrencyFormatter,
    createOptionsFormatter,
    DataTableCustomized,
    type DataTableAction,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import AddIcons from '@/icons/AddIcons'
import type { ServicePriceModalProps } from '@/modals/ServiceModals/ServicePriceModal'
import type { ServiceVatRateModalProps } from '@/modals/ServiceModals/ServiceVatRateModal'
import {
    deleteServicePrice,
    deleteServiceVatRate,
    getService,
    getServicePrices,
    getServiceVatRates,
} from '@/modules/Service/services'
import type { ServicePrice, ServiceVatRate } from '@/modules/Service/types'
import { formatDateWithFormatter } from '@/utils/function'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Button, Typogaphy } from 'vnetwork-ui'
import BundleConfigPrices from './BundleConfigPrices'

const getPriceColumns = (
    t: (key: string) => string
): DataTableColumn<ServicePrice>[] => [
    {
        key: 'service_name',
        title: t('service_name'),
        width: 160,
        render: (value) => value || '—',
    },
    {
        key: 'price_type',
        title: t('prices.price_type'),
        width: 150,
        formatter: createOptionsFormatter({
            options: [
                {
                    label: t('prices.recurring'),
                    value: 'recurring',
                    color: 'success',
                },
                {
                    label: t('prices.register'),
                    value: 'register',
                    color: 'primary',
                },
            ],
            renderMode: 'chip',
        }),
    },
    {
        key: 'price',
        title: t('prices.price'),
        width: 180,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'effective_from',
        title: t('prices.effective_from'),
        width: 140,
        cellClassName: 'text-right',
        render: (value) => (value ? formatDateWithFormatter(value) : '—'),
    },
    {
        key: 'effective_to',
        title: t('prices.effective_to'),
        cellClassName: 'text-right',
        width: 140,
        render: (value, row) =>
            row?.price_type === 'register'
                ? '—'
                : value
                  ? formatDateWithFormatter(value)
                  : t('prices.ongoing'),
    },
    {
        key: 'source',
        title: t('prices.source'),
        width: 120,
        render: (value) => <span className="capitalize">{value || '—'}</span>,
    },
    {
        key: 'description',
        title: t('prices.description'),
        width: 200,
        render: (value) => value || '—',
    },
    {
        key: 'created_at',
        title: t('created_at'),
        width: 140,
        render: (value) => (value ? formatDateWithFormatter(value) : '—'),
    },
]

const getVatColumns = (
    t: (key: string) => string
): DataTableColumn<ServiceVatRate>[] => [
    {
        key: 'source',
        title: t('vat.source'),
        width: 150,
        formatter: createOptionsFormatter({
            options: [
                {
                    label: t('vat.source_product_group'),
                    value: 'product_group',
                    color: 'success',
                },
                {
                    label: t('vat.source_service'),
                    value: 'service',
                    color: 'primary',
                },
            ],
            renderMode: 'chip',
        }),
    },
    {
        key: 'tax_rate',
        title: t('vat.tax_rate'),
        width: 120,
        cellClassName: 'text-right',
        render: (value) => `${(Number(value) * 100).toFixed(0)}%`,
    },
    {
        key: 'effective_from',
        title: t('vat.effective_from'),
        width: 140,
        render: (value) => (value ? formatDateWithFormatter(value) : '—'),
    },
    {
        key: 'effective_to',
        title: t('vat.effective_to'),
        width: 140,
        render: (value) =>
            value ? formatDateWithFormatter(value) : t('vat.ongoing'),
    },
    {
        key: 'description',
        title: t('vat.description'),
        width: 250,
        render: (value) => value || '—',
    },
    {
        key: 'created_at',
        title: t('created_at'),
        width: 140,
        render: (value) => (value ? formatDateWithFormatter(value) : '—'),
    },
]

const Prices = () => {
    const { id: serviceID } = useParams()
    const { t } = useTranslation('services')
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()

    const { data: service } = useQueryWithHandler({
        queryFn: () => getService(Number(serviceID)),
        queryKey: [QUERY_KEYS.SERVICE_DETAIL, Number(serviceID)],
    })

    const isPackage = service?.service_pricing_model === 'package'
    const isBundle = service?.service_pricing_model === 'bundle'

    const {
        data: prices,
        isLoading: pricesLoading,
        isFetching: pricesFetching,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_PRICES, Number(serviceID)],
        queryFn: () => getServicePrices(Number(serviceID)),
        enabled: isPackage,
    })

    const {
        data: vatRatesRaw,
        isLoading: vatLoading,
        isFetching: vatFetching,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_VAT_RATES, Number(serviceID)],
        queryFn: () => getServiceVatRates(Number(serviceID)),
    })

    const vatRates = useMemo(
        () =>
            [...(vatRatesRaw || [])].sort((a, b) =>
                b.effective_from.localeCompare(a.effective_from)
            ),
        [vatRatesRaw]
    )

    const { refetch } = useQueryClientCallback({
        reactQueryKeys: [QUERY_KEYS.SERVICES_PRICES],
    })

    const { refetch: refetchVat } = useQueryClientCallback({
        reactQueryKeys: [QUERY_KEYS.SERVICES_VAT_RATES],
    })

    const { mutateAsync: deleteMutation } = useMutationWithHandler(
        { mutationFn: deleteServicePrice },
        {
            successMessage: t('prices.delete_success'),
            reactQueryKeys: QUERY_KEYS.SERVICES_PRICES,
            onSuccess: () => refetch(),
        }
    )

    const { mutateAsync: deleteVatMutation } = useMutationWithHandler(
        { mutationFn: deleteServiceVatRate },
        {
            successMessage: t('vat.delete_success', {
                defaultValue: 'Xóa thuế VAT thành công',
            }),
            reactQueryKeys: QUERY_KEYS.SERVICES_VAT_RATES,
            onSuccess: () => refetchVat(),
        }
    )

    const priceColumns = useMemo(() => getPriceColumns(t), [t])
    const vatColumns = useMemo(() => getVatColumns(t), [t])

    // Actions for register price rows: edit & delete
    const priceActions: DataTableAction<ServicePrice>[] = useMemo(
        () => [
            {
                key: 'edit',
                label: t('prices.edit'),
                variant: 'info',
                hidden: (row) => row.price_type !== 'register',
                onClick: (row) =>
                    setModal<ServicePriceModalProps>(
                        'ServiceModals/ServicePriceModal/index',
                        { serviceID: Number(serviceID), price: row }
                    ),
            },
            {
                key: 'delete',
                label: t('common.delete'),
                variant: 'danger',
                hidden: (row) => row.price_type !== 'register',
                onClick: (row) =>
                    setConfirmation({
                        open: true,
                        title: t('common.delete'),
                        description: t('prices.delete_confirm'),
                        onConfirm: () =>
                            deleteMutation({
                                serviceID: Number(serviceID),
                                priceID: row.id,
                            }),
                    }),
            },
        ],
        [t, serviceID, setModal, setConfirmation, deleteMutation]
    )

    // Actions for VAT rows: edit & delete (only for source === 'service')
    const vatActions: DataTableAction<ServiceVatRate>[] = useMemo(
        () => [
            {
                key: 'edit',
                label: t('vat.edit', { defaultValue: 'Chỉnh sửa' }),
                variant: 'info',
                hidden: (row) => row.source !== 'service',
                onClick: (row) =>
                    setModal<ServiceVatRateModalProps>(
                        'ServiceModals/ServiceVatRateModal/index',
                        { serviceID: Number(serviceID), vatRate: row }
                    ),
            },
            {
                key: 'delete',
                label: t('common.delete'),
                variant: 'danger',
                hidden: (row) => row.source !== 'service',
                onClick: (row) =>
                    setConfirmation({
                        open: true,
                        title: t('common.delete'),
                        description: t('vat.delete_confirm', {
                            defaultValue:
                                'Bạn có chắc chắn muốn xóa thuế VAT này?',
                        }),
                        onConfirm: () =>
                            deleteVatMutation({
                                serviceID: Number(serviceID),
                                vatRateID: row.id,
                            }),
                    }),
            },
        ],
        [t, serviceID, setModal, setConfirmation, deleteVatMutation]
    )

    const addButton = (
        <Button
            key="add-price"
            onClick={() =>
                setModal<ServicePriceModalProps>(
                    'ServiceModals/ServicePriceModal/index',
                    {
                        serviceID: Number(serviceID),
                    }
                )
            }
            variant="outlined"
            size="small"
        >
            <AddIcons className="w-4 h-4 mr-1" />
            {t('prices.add')}
        </Button>
    )

    const addVatButton = (
        <Button
            key="add-vat"
            onClick={() =>
                setModal<ServiceVatRateModalProps>(
                    'ServiceModals/ServiceVatRateModal/index',
                    {
                        serviceID: Number(serviceID),
                    }
                )
            }
            variant="outlined"
            size="small"
        >
            <AddIcons className="w-4 h-4 mr-1" />
            {t('vat.add', { defaultValue: 'Thêm thuế VAT' })}
        </Button>
    )

    return (
        <div className="flex flex-col gap-4">
            {isPackage && (
                <Paper>
                    <Typogaphy className="text-primary font-medium mb-2 capitalize">
                        {t('prices.title')}
                    </Typogaphy>
                    <DataTableCustomized<ServicePrice>
                        columns={priceColumns}
                        rows={prices || []}
                        loading={pricesLoading}
                        isFetching={pricesFetching && !pricesLoading}
                        rowKey={(row) => row.id}
                        actions={priceActions}
                        toolbar={{
                            slotComponents: [addButton],
                        }}
                        emptyMessage={t('prices.no_data')}
                    />
                </Paper>
            )}
            {isBundle && <BundleConfigPrices serviceID={Number(serviceID)} />}
            <Paper>
                <Typogaphy className="text-primary font-medium mb-2 capitalize">
                    {t('vat.title')}
                </Typogaphy>
                <DataTableCustomized<ServiceVatRate>
                    columns={vatColumns}
                    rows={vatRates || []}
                    loading={vatLoading}
                    isFetching={vatFetching && !vatLoading}
                    rowKey={(row) => row.id}
                    actions={vatActions}
                    toolbar={{
                        slotComponents: [addVatButton],
                    }}
                    emptyMessage={t('vat.no_data')}
                />
            </Paper>
        </div>
    )
}

export default Prices
